import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Quote, Phone, Star } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSegment } from "@/context/CustomerSegmentContext";
import { getServicesBySegment } from "@/data/services";
import { heroImage, HERO_BACKGROUNDS, homeFeaturedProjects } from "@/data/projectImages";
import { beforeAfterProjects } from "@/data/projectData";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import GoogleReviewsBadge from "@/components/GoogleReviewsBadge";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const stagger = (i) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
});

const Home = () => {
  const { t, lang } = useT();
  const { segment, isPrivate } = useSegment();

  // Sort services by segment priority and take top 6
  const featuredServices = getServicesBySegment(segment).slice(0, 6);
  const featuredGallery = homeFeaturedProjects; // curated diverse 8 projects – no duplicates
  // Use the same source as the Referenzen page so edits stay consistent everywhere.
  const featuredBA = beforeAfterProjects.slice(0, 2);

  // Segment-aware hero copy for SEO
  const heroTitle = isPrivate ? t.home.hero_title_private : t.home.hero_title_commercial;
  const heroSubtitle = isPrivate ? t.home.hero_subtitle_private : t.home.hero_subtitle_commercial;

  return (
    <div data-testid="home-page">
      {/* ===== HERO – Full-bleed Beni Gipserei style ===== */}
      <section className="relative border-b border-zinc-200 overflow-hidden" data-testid="hero-section">
        {/* Full-bleed background image */}
        <div className="absolute inset-0 z-0">
          <motion.img
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            src={heroImage}
            alt="SF-Maler Referenz – helle Wohnung Basel"
            className="w-full h-full object-cover"
          />
          {/* Soft white gradient on the left for text readability (Beni style) */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-transparent lg:via-white/70 lg:to-white/0"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent"></div>
        </div>

        {/* Content overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[560px] sm:min-h-[640px] lg:min-h-[760px]">
            {/* LEFT - Text Column */}
            <div className="lg:col-span-7 flex flex-col justify-center py-14 sm:py-20 lg:py-28">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              >
                <span className="text-overline" data-testid="hero-overline">{t.home.hero_overline}</span>
                <h1 key={heroTitle} className="mt-4 sm:mt-5 text-[2rem] leading-[1.05] sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-extrabold text-zinc-900 tracking-tighter" data-testid="hero-title">
                  {heroTitle}
                </h1>
                <p key={heroSubtitle} className="mt-4 sm:mt-6 lg:mt-7 text-sm sm:text-base lg:text-lg text-zinc-700 leading-relaxed max-w-xl" data-testid="hero-subtitle">
                  {heroSubtitle}
                </p>
                <div className="mt-7 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4" data-testid="hero-ctas">
                  <Link to="/kontakt" className="btn-primary btn-glow" data-testid="hero-cta-primary">
                    {t.common.cta_contact} <ArrowRight size={16} />
                  </Link>
                  <Link to="/referenzen" className="btn-outline" data-testid="hero-cta-secondary">
                    {t.common.cta_more}
                  </Link>
                </div>

                {/* Google Reviews Badge */}
                <div className="mt-7 sm:mt-10">
                  <GoogleReviewsBadge />
                </div>

                <div className="mt-7 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-xl border-t border-zinc-300/60 pt-6 sm:pt-7">
                  {[t.home.hero_trust1, t.home.hero_trust2, t.home.hero_trust3].map((trust, i) => (
                    <motion.div
                      key={i}
                      {...stagger(i + 1)}
                      className="flex items-start gap-2"
                      data-testid={`hero-trust-${i}`}
                    >
                      <CheckCircle2 size={16} className="text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-semibold text-zinc-800 leading-snug">{trust}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* RIGHT - Floating "DIREKT" call card */}
            <div className="lg:col-span-5 relative">
              <motion.a
                href="tel:+41796070148"
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                className="hidden lg:flex absolute bottom-10 right-0 bg-white border-l-4 border-red-600 px-6 py-5 shadow-2xl hover:shadow-red-500/30 transition-all hover:-translate-y-1 group items-center gap-5"
                data-testid="hero-direct-card"
              >
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold">Direkt</div>
                  <div className="font-display text-2xl xl:text-3xl text-zinc-900 font-extrabold tracking-tight group-hover:text-red-600 transition-colors">079 607 01 48</div>
                </div>
                <div className="w-12 h-12 bg-red-600 text-white flex items-center justify-center group-hover:bg-zinc-900 transition-colors flex-shrink-0">
                  <Phone size={20} />
                </div>
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES WITH PHOTOS ===== */}
      <section className="py-14 sm:py-20 lg:py-32 bg-zinc-50 border-b border-zinc-200" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-overline">{t.home.services_overline}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
              {t.home.services_title}
            </h2>
            <p className="mt-5 text-base text-zinc-600 leading-relaxed">{t.home.services_subtitle}</p>
          </motion.div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {featuredServices.map((s, i) => {
              const Icon = s.icon;
              const item = t.services.items[s.key];
              return (
                <motion.div key={s.slug} {...stagger(i)}>
                  <Link
                    to={`/dienstleistungen/${s.slug}`}
                    className="group block relative bg-white border border-zinc-200 overflow-hidden card-lift"
                    data-testid={`service-card-${s.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden relative bg-gradient-to-br from-zinc-100 to-zinc-200">
                      {s.image ? (
                        <>
                          <img src={s.image} alt={item.title} className="w-full h-full object-cover img-zoom" />
                          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 via-zinc-900/20 to-transparent"></div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Icon size={72} className="text-zinc-300 group-hover:text-red-600/30 transition-colors" strokeWidth={1} />
                        </div>
                      )}
                      <div className="absolute top-4 left-4 w-12 h-12 bg-white flex items-center justify-center shadow-lg group-hover:bg-red-600 transition-colors duration-300 z-10">
                        <Icon size={22} className="text-red-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                      </div>
                      <h3 className={`absolute bottom-4 left-4 right-4 font-display font-bold text-xl z-10 ${s.image ? "text-white" : "text-zinc-900"}`}>{item.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-zinc-600 leading-relaxed">{item.short}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-zinc-900 group-hover:text-red-600 transition-colors">
                        {t.common.learn_more}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="mt-12 text-center">
            <Link to="/dienstleistungen" className="btn-outline" data-testid="services-all-link">
              {t.services.title} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="py-14 sm:py-20 lg:py-32 border-b border-zinc-200" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-overline">{t.home.why_overline}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
              {t.home.why_title}
            </h2>
            <p className="mt-5 text-base text-zinc-600 leading-relaxed">{t.home.why_subtitle}</p>
          </motion.div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 sm:gap-x-12 gap-y-8 sm:gap-y-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <motion.div
                key={i}
                {...stagger(i)}
                className="flex gap-5 border-t border-zinc-200 pt-6 group hover:border-red-600 transition-colors"
                data-testid={`why-item-${i}`}
              >
                <div className="font-display text-3xl font-bold text-red-600 leading-none transition-transform group-hover:scale-110 origin-left">0{i}</div>
                <div>
                  <h3 className="font-display font-bold text-lg text-zinc-900">{t.home[`why_${i}_title`]}</h3>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{t.home[`why_${i}_text`]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BEFORE / AFTER (only 2) - LIGHT bg for text readability ===== */}
      <section className="py-14 sm:py-20 lg:py-32 bg-zinc-50 border-b border-zinc-200 relative overflow-hidden" data-testid="ba-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-overline">{t.home.ba_overline}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
              {t.home.ba_title}
            </h2>
            <p className="mt-5 text-base text-zinc-600 leading-relaxed">{t.home.ba_subtitle}</p>
          </motion.div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10">
            {featuredBA.map((ba, i) => (
              <motion.div key={ba.id} {...stagger(i)}>
                <BeforeAfterSlider before={ba.before} after={ba.after} title={ba.title[lang]} description={ba.description[lang]} aspectClass={ba.id === "fassade-renovation-neu" ? "aspect-[9/16]" : ba.id === "balkon1" ? "aspect-[3/4]" : "aspect-[4/3]"} />
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-14 text-center">
            <Link
              to="/referenzen"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-7 py-4 font-bold hover:bg-red-600 transition-all hover:translate-y-[-2px]"
              data-testid="ba-all-link"
            >
              {t.home.gallery_cta} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== GALLERY PREVIEW ===== */}
      <section className="py-14 sm:py-20 lg:py-32 border-b border-zinc-200" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <span className="text-overline">{t.home.gallery_overline}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
                {t.home.gallery_title}
              </h2>
              <p className="mt-5 text-base text-zinc-600 leading-relaxed">{t.home.gallery_subtitle}</p>
            </div>
            <Link to="/referenzen" className="text-sm font-bold text-zinc-900 inline-flex items-center gap-2 hover:text-red-600 transition-colors" data-testid="gallery-preview-link">
              {t.home.gallery_cta} <ArrowRight size={14} />
            </Link>
          </motion.div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {featuredGallery.map((img, i) => (
              <motion.div
                key={img.id}
                {...stagger(i)}
                className="aspect-square overflow-hidden bg-zinc-100 group relative cursor-pointer"
                data-testid={`gallery-thumb-${img.id}`}
              >
                <img src={img.url} alt={img.title} className="w-full h-full object-cover img-zoom" loading="lazy" />
                <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/60 transition-all duration-300 flex items-end p-4">
                  <span className="text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">{img.title}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS + GOOGLE REVIEWS BUTTON ===== */}
      <section className="py-14 sm:py-20 lg:py-32 bg-zinc-50 border-b border-zinc-200" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <span className="text-overline">{t.home.testimonials_overline}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
                {t.home.testimonials_title}
              </h2>
            </div>

            {/* Google Reviews Badge - shared component */}
            <GoogleReviewsBadge />
          </motion.div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {t.testimonials.map((q, i) => (
              <motion.div
                key={i}
                {...stagger(i)}
                className="bg-white p-6 sm:p-10 border border-zinc-200 flex flex-col card-lift"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} size={14} className="fill-yellow-400 text-yellow-400" />)}
                </div>
                <Quote size={28} className="text-red-600" />
                <blockquote className="mt-6 text-base text-zinc-800 leading-relaxed flex-1">&ldquo;{q.quote}&rdquo;</blockquote>
                <div className="mt-8 border-t border-zinc-200 pt-5">
                  <div className="font-display font-bold text-zinc-900">{q.author}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">{q.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION with background image ===== */}
      <section className="py-14 sm:py-20 lg:py-32 relative overflow-hidden" data-testid="home-cta-section">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img src={HERO_BACKGROUNDS.cta} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-red-600/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-red-900/40 via-transparent to-red-900/20"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.12, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="absolute -top-32 -left-32 w-96 h-96 bg-white rounded-full blur-3xl"
        ></motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.08, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute -bottom-32 -right-32 w-96 h-96 bg-white rounded-full blur-3xl"
        ></motion.div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 text-white">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter drop-shadow-lg">
            {t.home.cta_section_title}
          </motion.h2>
          <motion.p {...fadeUp} className="mt-6 text-lg text-red-50 leading-relaxed max-w-2xl mx-auto">
            {t.home.cta_section_text}
          </motion.p>
          <motion.div {...fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/kontakt" className="bg-white text-red-600 px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-zinc-100 transition-all hover:translate-y-[-3px] hover:shadow-2xl" data-testid="home-cta-contact">
              {t.common.cta_offer} <ArrowRight size={16} />
            </Link>
            <a href="tel:+41796070148" className="border-2 border-white text-white px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-white hover:text-red-600 transition-all hover:translate-y-[-3px]" data-testid="home-cta-call">
              <Phone size={16} /> 079 607 01 48
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
